import { z } from 'zod';
import { apiLogEntrySchema, dashboardSummarySchema, deviceAssignmentTypeSchema, deviceSchema, locationPointSchema, sessionInfoSchema, } from './models.js';
/* ------------------------------------------------------------------ *
 * Auth
 * ------------------------------------------------------------------ */
export const credentialsSchema = z.object({
    cid: z.string().trim().min(1, 'CID requis'),
    username: z.string().trim().min(1, "Nom d'utilisateur requis"),
    password: z.string().trim().min(1, 'Mot de passe requis'),
});
export const registerRequestSchema = credentialsSchema.extend({
    email: z.string().email().optional(),
    phone: z.string().optional(),
});
export const loginRequestSchema = credentialsSchema;
export const authResultSchema = z.object({
    session: sessionInfoSchema,
});
/* ------------------------------------------------------------------ *
 * Devices
 * ------------------------------------------------------------------ */
export const deviceNumSchema = z
    .string()
    .min(4, 'deviceNum invalide')
    .regex(/^[A-Za-z0-9_-]+$/, 'deviceNum invalide');
export const bindRequestSchema = z.object({
    deviceNum: deviceNumSchema,
    name: z.string().min(1).optional(),
    /** Confirmation explicite requise en mode réel (jamais exigée en mock). */
    confirmReal: z.boolean().optional().default(false),
});
export const unbindRequestSchema = z.object({
    deviceNum: deviceNumSchema,
    /** Confirmation explicite requise en mode réel (jamais exigée en mock). */
    confirmReal: z.boolean().optional().default(false),
});
/**
 * Une association/dissociation acceptée par Vernal n'est pas immédiatement
 * visible dans getDeviceList : `pending` tant qu'une relecture ne l'a pas
 * confirmée, `verified` une fois la liste fournisseur contrôlée.
 */
export const operationVerificationSchema = z.enum(['verified', 'pending']);
export const bindResultSchema = z.object({
    deviceNum: z.string(),
    device: deviceSchema.nullable(),
    verification: operationVerificationSchema,
    message: z.string(),
});
export const unbindResultSchema = z.object({
    deviceNum: z.string(),
    verification: operationVerificationSchema,
    message: z.string(),
});
/** Capacités serveur exposées au frontend pour piloter l'UI sans deviner la config. */
export const capabilitiesResultSchema = z.object({
    realBindUnbindAllowed: z.boolean(),
});
export const refreshLocationRequestSchema = z.object({
    deviceNum: deviceNumSchema,
});
export const refreshLocationsRequestSchema = z.object({
    deviceNums: z.array(deviceNumSchema).min(1, 'Au moins un device requis').max(5000, 'Maximum local : 5 000 appareils'),
    confirmReal: z.boolean().optional().default(false),
});
export const locationHistoryQuerySchema = z.object({
    deviceNum: deviceNumSchema,
    /** ISO 8601; par défaut J-7 */
    from: z.string().optional(),
    /** ISO 8601; par défaut maintenant */
    to: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(200),
});
export const deviceListResultSchema = z.object({
    devices: z.array(deviceSchema),
    knownDevicesFound: z.array(z.string()),
    current: z.number(),
    pages: z.number(),
    size: z.number(),
    total: z.number(),
});
export const localDeviceMetadataUpdateSchema = z
    .object({
    localName: z.union([z.string().trim().min(1, 'Nom local requis').max(60, 'Maximum 60 caractères'), z.null()]).optional(),
    notes: z.string().trim().max(2_000, 'Maximum 2 000 caractères').optional(),
    assignmentType: deviceAssignmentTypeSchema.optional(),
    assignmentLabel: z.union([z.string().trim().max(120, 'Maximum 120 caractères'), z.null()]).optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, 'Au moins une modification est requise');
export const locationHistoryResultSchema = z.object({
    deviceNum: z.string(),
    from: z.string(),
    to: z.string(),
    points: z.array(locationPointSchema),
});
export const refreshResultSchema = z.object({
    deviceNum: z.string(),
    requested: z.boolean(),
    location: locationPointSchema.nullable(),
    device: deviceSchema.nullable(),
    message: z.string(),
});
export const refreshManyResultSchema = z.object({
    results: z.array(refreshResultSchema),
    summary: z.object({ requested: z.number(), accepted: z.number(), failed: z.number(), batchCount: z.number() }).optional(),
    batches: z.array(z.object({ index: z.number(), requested: z.number(), accepted: z.number(), failed: z.number() })).optional(),
});
/* ------------------------------------------------------------------ *
 * Health / Debug
 * ------------------------------------------------------------------ */
export const healthResultSchema = z.object({
    status: z.literal('ok'),
    service: z.string(),
    version: z.string(),
    uptimeSeconds: z.number(),
    timestamp: z.string(),
    mockMode: z.boolean(),
    vernalBaseUrl: z.string(),
    cryptoConfigured: z.boolean(),
});
export const debugLogsResultSchema = z.object({
    logs: z.array(apiLogEntrySchema),
    total: z.number(),
});
export const dashboardResultSchema = dashboardSummarySchema;
//# sourceMappingURL=contracts.js.map